import { z } from 'zod';
import { isoDateSchema, softDeletableSchema, uuidSchema } from './common';

// Enrollment — many-to-many join between dancer and class scoped to a term.

export const enrollmentStatusSchema = z.enum(['enrolled', 'waitlisted', 'dropped']);
export type EnrollmentStatus = z.infer<typeof enrollmentStatusSchema>;

export const enrollmentInputSchema = z.object({
  dancer_id: uuidSchema,
  class_id: uuidSchema,
  status: enrollmentStatusSchema.default('enrolled'),
  start_date: isoDateSchema,
  end_date: isoDateSchema.nullable().optional(),
  // Pre-computed per-enrollment tuition snapshot, after sibling-discount logic.
  applied_tuition_cents: z.number().int().nonnegative(),
});
export type EnrollmentInput = z.infer<typeof enrollmentInputSchema>;

export const enrollmentSchema = softDeletableSchema.merge(enrollmentInputSchema);
export type Enrollment = z.infer<typeof enrollmentSchema>;

// Sibling-discount calculation — pure function used by both UI (preview)
// and the Edge Function that writes charges.
// Studio convention (configurable per studio later):
//   1st dancer: full price; 2nd: 10% off; 3rd+: 15% off each.
export const SIBLING_DISCOUNT_TIERS = [
  { dancerIndex: 0, discountPercent: 0 },
  { dancerIndex: 1, discountPercent: 10 },
  { dancerIndex: 2, discountPercent: 15 },
] as const;

export function applySiblingDiscount(
  fullTuitionCents: number,
  dancerIndexInFamily: number,
): number {
  const tier =
    SIBLING_DISCOUNT_TIERS[Math.min(dancerIndexInFamily, SIBLING_DISCOUNT_TIERS.length - 1)];
  const discountPercent = tier?.discountPercent ?? 15;
  return Math.round(fullTuitionCents * (1 - discountPercent / 100));
}
